'use client';

import React, { useCallback, useEffect, useRef, useState, useMemo, useContext, Suspense } from 'react';
import { AppBar, Toolbar, Box, Stack, IconButton, SwipeableDrawer, Container, useTheme } from '@mui/material';
import { dropDownStyles, BurgerMenuStyled } from './Header.styled';

import logo from '../../assets/images/svg/logo.svg';
import subLogo from '../../assets/images/img/sub_logo.png';
import companyName from '../../assets/images/svg/companyName.svg';
import { getSuggestions } from '../../api/products';
import useDebounce from '../../hooks/debounceHook';
import { useOnClickOutside } from '../../hooks/mouseHooks';
import { SuggestionType } from '../../react-pages/Catalog/types';
import { useRouter } from 'next/navigation';
import { FavoriteFilledIcon } from '../../icons/FavoriteFilledIcon';
import { useAppDispatch, useAppSelector } from '../../hooks/reduxHooks';
import { reducerTypes } from '../../store/Alert/types';
import { setSelectedCurrencyAction } from '../../store/SelectedCurrency/action';
import { SidebarList } from './SidebarList';
import Image from 'next/image';
import HeartIconSVG from '../../assets/images/svg/heartHeaderIcon.svg';
import NavbarMenuSVG from '../../assets/images/svg/navbarmenuicon.svg';
import SearchIconSVG from '../../assets/images/svg/searchHeaderIcon.svg';
import { ResponsiveSearchBar } from './ResponsiveSearchBar';
import dynamic from 'next/dynamic';
import { LanguageContext } from '../../providers/LanguageProvider';
import LocalizedLink from '../Link/localized-link';
import { buildRoute } from '../../utils/routerPathBuilder';
import { LanguageSelect } from './LanguageSelect';

const ReactSelect = dynamic(() => import('react-select'), { ssr: true });

interface NavbarProps {
  headerItems: {
    title: string;
    path: string;
    icon: string;
  }[];
}
export const Navbar: React.FC<NavbarProps> = ({ headerItems }) => {
  const router = useRouter();
  const dispatch = useAppDispatch();
  const searchRef = useRef<HTMLDivElement>(null);

  const locale = useAppSelector((st) => st.locale);
  const selectedCurrency = useAppSelector((st) => st.currency.selectedCurrency);
  const currency = useAppSelector((st) => st.currency.currency);

  const languageContext = useContext(LanguageContext);
  const languages = useAppSelector((st) => st.selectedLang.languages);

  const [sideBar, setSideBar] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [, setSidebarItems] = useState<{ title: string; path: string; icon?: string }[]>([]);
  const [suggestions, setSuggestions] = useState<SuggestionType[]>([]);
  const debouncedValue = useDebounce<string>(searchText, 500);

  const [searchVisible, setSearchVisible] = useState(false);
  const { favorites } = useAppSelector((st) => st.products);
  const theme = useTheme();

  const messages = useMemo(() => {
    return {
      header_items: headerItems.map((item, index) => ({
        ...item,
        title: locale[`header_items_${index}`],
      })),
      header_item_sell: locale['header_items_sell'],
      header_item_trade: locale['header_items_trade'],
      header_wishlist_header: locale['header_wishlist_header'],
      header_wishlist_text: locale['header_wishlist_text'],
      header_search_placeholder: locale['header_search_placeholder'],
    };
  }, [headerItems, locale]);

  useOnClickOutside(searchRef, () => setSearchVisible(false), 'search-box');

  useEffect(() => {
    if (searchVisible) return;
    setSearchText('');
  }, [searchVisible]);

  useEffect(() => {
    if (debouncedValue) {
      getSearchSuggestions();
    } else {
      setSuggestions([]);
    }

    async function getSearchSuggestions() {
      const response = await getSuggestions(debouncedValue);
      setSuggestions(() => response?.data?.items || []);
    }
  }, [debouncedValue]);

  function goToWishlist() {
    if (!favorites.length) {
      dispatch({
        type: reducerTypes.TOGGLE_ALERT,
        payload: {
          header: messages.header_wishlist_header,
          text: messages.header_wishlist_text,
          noIcon: true,
          delay: 10000,
        },
      });
      return;
    }
    router.push(buildRoute(`/wishlist`, languageContext.lang));
  }

  useEffect(() => setSidebarItems(messages.header_items), [messages]);

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setSideBar(open);
      setSidebarItems((prev) =>
        prev.filter((item) => item.title !== messages.header_item_sell && item.title !== messages.header_item_trade)
      );
    },
    [messages.header_item_sell, messages.header_item_trade]
  );

  const CurrencySelectMenu = useCallback(
    (styles?: any) => {
      if (!currency) return null;
      return (
        <ReactSelect
          className='custom-select currency-select'
          options={currency}
          value={currency.find((item) => item.id === selectedCurrency)}
          styles={styles ? styles : dropDownStyles}
          isClearable={false}
          isSearchable={false}
          instanceId={'currency-select'}
          classNamePrefix='react-select'
          aria-label='Select currency'
          onChange={(option: any) => {
            if (!option) return;
            dispatch(setSelectedCurrencyAction(option?.id));
            let url = new URL(window.location.href);
            url.searchParams.set('curr', option?.id);
            router.push(url.toString());
          }}
          theme={(theme) => {
            return {
              ...theme,
              borderRadius: 0,
              colors: {
                ...theme.colors,
                text: 'white',
                primary: 'black',
              },
            };
          }}
        />
      );
    },
    [currency, dispatch, router, selectedCurrency]
  );

  const LanguageSelectMenu = useCallback(
    (styles?: any) => {
      if (!languages) return null;
      return (
        <Suspense>
          <LanguageSelect />
        </Suspense>
      );
    },
    [languages]
  );

  const sidebarList = useCallback(
    () => (
      <SidebarList
        headerItems={headerItems}
        toggleDrawer={toggleDrawer}
        CurrencySelectMenu={CurrencySelectMenu}
        LanguageSelectMenu={LanguageSelectMenu}
        setSideBar={setSideBar}
      />
    ),
    [headerItems, toggleDrawer, CurrencySelectMenu, LanguageSelectMenu]
  );

  return (
    <Container maxWidth='lg1286'>
      <AppBar
        position='static'
        elevation={0}
        sx={{ position: 'relative', background: 'white', color: 'black', flexDirection: 'column' }}
      >
        <Toolbar
          disableGutters
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            flex: 1,
            '& .react-select__value-container': { paddingLeft: 0 },
          }}
        >
          <BurgerMenuStyled>
            <IconButton
              aria-label='Open Menu'
              aria-controls='menu-appbar'
              onClick={toggleDrawer(true)}
              sx={{ p: '24px', margin: '-24px' }}
            >
              <Image src={NavbarMenuSVG} alt='Open menu' />
            </IconButton>
            <SwipeableDrawer anchor={'left'} open={sideBar} onClose={toggleDrawer(false)} onOpen={toggleDrawer(true)}>
              {sidebarList()}
            </SwipeableDrawer>
          </BurgerMenuStyled>
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            {CurrencySelectMenu()}
            {LanguageSelectMenu()}
          </Box>
          {/* <Box sx={{ display: { xs: 'none', md: 'flex' } }}></Box> */}
          <Box
            sx={{
              marginLeft: '-40px',
              [theme.breakpoints.down('md')]: {
                position: 'absolute',
                left: 'calc(50% - 30px)',
                marginLeft: 0,
              },
            }}
          >
            <LocalizedLink
              href={'/'}
              title='Chrono 10:10'
              style={{
                display: 'flex',
                alignItems: 'baseline',
                flexDirection: 'column',
                alignContent: 'center',
                WebkitAlignItems: 'center',
              }}
            >
              <div style={{ display: 'flex', minHeight: 50 }}>
                <Image src={logo} alt='Chrono 10:10 logo' className='mobileLogo' height={50} priority />
                <Box
                  sx={{ display: { xs: 'none', md: 'flex' }, height: 1, flexDirection: 'column', marginTop: '10px' }}
                >
                  <Image
                    style={{ marginBottom: '0px' }}
                    src={companyName}
                    alt='Chrono 10:10 logo'
                    className='logo'
                    width={190}
                    priority
                  />
                  <Image
                    src={subLogo}
                    priority
                    alt='Watch Boutique & Service'
                    style={{ marginTop: '5px', height: '13px', width: '190px' }}
                  />
                </Box>
              </div>
            </LocalizedLink>
          </Box>
          <Stack flexDirection={'row'} gap='16px' sx={{ marginLeft: { xs: 'auto', md: '0px' } }}>
            <Box
              onClick={() => setSearchVisible(true)}
              sx={{ position: { xs: '', md: 'relative' }, display: 'flex', alignItems: 'center' }}
            >
              <Box sx={{ cursor: 'pointer' }}>
                <Image src={SearchIconSVG} alt='Search' />
              </Box>
              <ResponsiveSearchBar
                stackRef={searchRef}
                suggestions={suggestions}
                searchText={searchText}
                searchVisible={searchVisible}
                setSearchText={setSearchText}
              />
            </Box>
            <Box component='span' onClick={goToWishlist} sx={{ cursor: 'pointer' }}>
              {favorites?.length > 0 ? (
                <FavoriteFilledIcon size={18} />
              ) : (
                <Image src={HeartIconSVG} alt='Favorites' width={18} height={16} />
              )}
            </Box>
            {favorites?.length ? (
              <Box component='span' className='wishlist-count'>
                <Box component='span'>{favorites.length}</Box>
              </Box>
            ) : null}
          </Stack>
        </Toolbar>
      </AppBar>
    </Container>
  );
};
